import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { i18next } from "../i18n";

export interface PageMeta {
	title?: string;
	description?: string;
}

/**
 * Writes the tags and returns the restore. Exported so the set/restore pair can be exercised
 * without a React renderer — the repo has no DOM test setup.
 */
export function applyPageMeta({ title, description }: PageMeta): () => void {
	const tag = document.querySelector('meta[name="description"]');
	const previousDescription = tag?.getAttribute("content") ?? null;
	if (title) document.title = title;
	if (description) tag?.setAttribute("content", description);
	return () => {
		// Back to the site-wide defaults: the title is the one i18n's languageChanged handler sets,
		// so a language switch after the page unmounts still lands on the right string.
		document.title = i18next.t("app.title");
		if (description && previousDescription !== null) {
			tag?.setAttribute("content", previousDescription);
		}
	};
}

/** Per-page `<title>` and `<meta name="description">`, restored to the site defaults on unmount. */
export function usePageMeta({ title, description }: PageMeta) {
	// `language` is a dependency even though `title` usually changes with it: i18n's languageChanged
	// handler resets the title site-wide, and a page whose copy reads the same in both locales would
	// otherwise never re-apply its own.
	const { i18n } = useTranslation();
	useEffect(() => {
		if (!title && !description) return;
		return applyPageMeta({ title, description });
	}, [i18n.language, title, description]);
}
